<template>
	<div id="dataset-manage-page" class="overflow-y-auto hidden-scrollbar">

		<!-- Title & Buttons -->
		<div class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center">

			<div class="inline-block text-2xl font-medium mr-4">{{$t('Datasets')}}</div>

			<div class="dropdown" data-placement="bottom-start">
				<button class="btn px-2 box text-gray-700 hover:bg-green-600 hover:text-white" @click="showCreateDataset = true">
					<span class="w-5 h-5 flex items-center justify-center"><PlusIcon class="w-4 h-4" /></span>
				</button>
			</div>

			<button class="btn px-2 box text-gray-700 ml-1 hover:bg-blue-500 hover:text-white" @click="reset()">
				<span class="w-5 h-5 flex items-center justify-center"><RefreshCcwIcon class="w-4 h-4" /></span>
			</button>

			<!-- Hide Datasets -->
			<button v-if="showHideDatasets" class="btn px-2 box text-gray-700 ml-1 hover:bg-blue-500 hover:text-white" @click="hideDatasets()">
				<span class="w-5 h-5 flex items-center justify-center"><EyeIcon class="w-4 h-4" /></span>
			</button>

		</div>

		<!-- Filters -->
		<div v-if="layout.options" class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-5 xl:mt-0" style="min-width: 700px;">
			
			<div class="mx-auto text-gray-600">

				<div class="px-10 py-3 bg-white rounded-full border" :class="layout.options.type || layout.options.trained || layout.options.search ? 'pl-10' : ''">
					
					<div class="relative w-48 mr-2 inline-block align-top">
						<SearchIcon v-if="!layout.options.search" class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600" style="stroke-width: 2.5;" />
						<SearchIcon v-else class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white" style="stroke-width: 2.5;" />
						<input type="text" class="w-full form-control p-2 pl-10 bg-white" :disabled="layout.loading"
							v-model="layout.options.search" :placeholder="$t('Search datasets...')" 
							:class="layout.options.search ? layout.loading ? 'bg-blue-500' : 'bg-blue-500 text-white' : 'text-gray-600'">
					</div>

					<select class="w-72 form-select p-2 cursor-pointer bg-white align-top" :disabled="layout.loading"
						v-model="layout.options.type" :class="layout.options.type ? layout.loading ? 'bg-blue-500' : 'bg-blue-500 text-white' : 'text-gray-600'">
							<option value="">{{$t('All types')}}</option>
							<option value="MULTICLASS">{{$t('Classification')}}</option>
							<option value="ANOMALY">{{$t('Anomaly detection')}}</option>
							<option value="imageObjectDetection">{{$t('Object detection')}}</option>
							<option value="MULTILABEL">{{$t('Image segmentation')}}</option>
					</select>

					<select class="w-44 form-select p-2 cursor-pointer ml-2 bg-white align-top" :disabled="layout.loading"
						v-model="layout.options.trained" :class="layout.options.trained ? layout.loading ? 'bg-blue-500' : 'bg-blue-500 text-white' : 'text-gray-600'">
							<option value="">{{$t('All')}}</option>
							<option value="true">{{$t('Trained')}}</option>
					</select>

					<select class="w-32 form-select p-2 cursor-pointer ml-2 text-gray-600 bg-white align-top" 
						v-model="layout.options.order" :disabled="layout.loading">
							<option value="desc">{{$t('Date desc')}}</option>
							<option value="asc">{{$t('Date asc')}}</option>
					</select>

					<button class="btn px-1 box bg-gray-600 mt-0.5 ml-4 hover:bg-blue-500 text-white h-8" @click="reset()" title="Reset Filters" :class="layout.options.search || layout.options.type || layout.options.trained ? '' : 'hidden'">
						<span class="w-5 h-5 flex items-center justify-center"><TrashIcon class="w-4 h-4" /></span>
					</button>

				</div>

			</div>

		</div>

		<!-- Datasets -->
		<div v-if="!layout.loading">

			<!-- Datasets count -->
			<div class="mt-5 w-full">
				<div class="text-gray-600 text-center text-xs">{{$t('Showing')}} {{ Object.keys(layout.datasetsList).length }} {{ $t('datasets') }}</div>
			</div>

			<!-- Datasets list -->
			<div class="mt-6 grid grid-cols-12 gap-3 mb-60">

				<div v-for="(dataset) in layout.datasetsList" :key="dataset.id" class="col-span-12 md:col-span-6 lg:col-span-4 xxl:col-span-3">
					
					<div class="box hover:bg-gray-100 border-t-4 p-5 cursor-pointer" :class="dataset.trained ? 'border-green-500' : 'border-gray-500'" @click="loadDataset(dataset.id)">

						<div class="flex items-center gap-4">

							<!-- Image -->
							<div class="w-16 h-16 xxl:w-20 xxl:h-20 image-fit shrink-0 mr-3">
								<img
									:src="dataset?.preview || require('@/assets/images/rosepetal/icon/logoLoadingGray.png')"
									:class="[
										'w-full h-full rounded-full',
										dataset?.preview ? 'zoom-in' : 'border border-gray-300'
									]"
								/>
							</div>

							<div class="flex-1">
								
								<!-- Header -->
								<div class="flex items-center justify-between">
									
									<a v-if="dataset.name" class="font-medium text-base truncate max-w-[70%]">
										{{ dataset.name.slice(0, 20) }}
										<span v-if="dataset.name.length > 20">...</span>
									</a>

									<!-- Options -->
									<div class="relative" :ref="(el) => dropdownRefs[dataset.id] = el">
										<div class="btn" @click.stop="activeDropdownId = activeDropdownId === dataset.id ? null : dataset.id">
											<MoreHorizontalIcon class="w-3 h-auto" />
										</div>

										<div v-if="activeDropdownId === dataset.id" class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-50">
											<div class="py-1">
												<a @click.stop="layout.selectedDataset = dataset; showDownloadDataset = true; closeDropdown()" class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
													<DownloadIcon class="w-4 h-4 mr-3" />
													{{ $t('Download') }}
												</a>

												<a @click.stop="layout.selectedDataset = dataset; showDeleteDataset = true; closeDropdown()" class="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer">
													<TrashIcon class="w-4 h-4 mr-3" />
													{{ $t('Delete') }}
												</a>
											</div>
										</div>
									</div>

								</div>

								<!-- Type-->
								<div class="flex justify-between items-center gap-2 text-gray-600 text-xs mt-3">
									<span>{{ dataset.typeName }}</span>
									<CodesandboxIcon v-if="dataset?.trained" class="w-5 h-5 text-green-600 mr-2" title="Trained"/>
								</div>

								<!-- Dates -->
								<div class="flex flex-col text-gray-600 text-xs font-italic mt-0.5" style="font-size: 11px">
									{{ $h.getTimestampDate(dataset.createdAt.toDate(), 'date') }}
									<span>
										<span v-if="dataset.updatedAt">
											(Last update: {{ $h.getTimestampDate(dataset.updatedAt.toDate(), 'date') }})
										</span>
										<span v-else>&nbsp;</span>
									</span>
								</div>

								<!-- Information-->
								<div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700 mt-3 rp-text-blue-1">
									<div @click.stop="loadDataset(dataset.id, 'upload')" class="flex items-center cursor-pointer rounded-sm transition-all duration-200 ease-in-out transform hover:scale-105" style="color: inherit;" onmouseover="this.style.color='#1e4591'" onmouseout="this.style.color='inherit'"><ImageIcon class="w-4 h-4 mr-1" /> {{ dataset.imagesCount }} {{ $t('Images') }}</div>
									<div @click.stop="loadDataset(dataset.id, 'tags')" class="flex items-center cursor-pointer rounded-sm transition-all duration-200 ease-in-out transform hover:scale-105" style="color: inherit;" onmouseover="this.style.color='#1e4591'" onmouseout="this.style.color='inherit'"><TagIcon class="w-4 h-4 mr-1" /> {{ dataset.tagsCount }} {{ $t('Tags') }}</div>
									<div @click.stop="loadDataset(dataset.id, 'models')" class="flex items-center cursor-pointer rounded-sm transition-all duration-200 ease-in-out transform hover:scale-105" style="color: inherit;" onmouseover="this.style.color='#1e4591'" onmouseout="this.style.color='inherit'"><CpuIcon class="w-4 h-4 mr-1" /> {{ dataset.modelsCount }} {{ $t('Models') }}</div>
								</div>

							</div>

						</div>

					</div>

				</div>

			</div>

			<!-- No results -->
			<div v-if="!Object.keys(layout.datasetsList).length" class="text-center justify-center mt-20 text-gray-600">
				<div>
					<SearchIcon class="w-12 h-12" /> <h2 class="text-lg truncate mt-2 font-medium">{{ $t('No results found') }}</h2>
					<div>{{ $t("Sorry, that filter combination has no results") }}</div>
					<div class="mt-2 text-blue-500 cursor-pointer" @click="reset()">{{$t('reset filters')}}</div>
				</div>
			</div>

		</div>

		<!-- Loading datasets -->
		<div v-else>
			<div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center p-5 mt-10">
				<div class="flex items-center justify-center">
					<img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-24" />
				</div>
				<LoadingIcon icon="three-dots" class="w-8 h-8" />
			</div>
		</div>

		<!-- Preview dataset -->
		<div v-if="layout.dataset">
			<DatasetCmp 
				:dataset="layout.dataset"
				:datasetsList="layout.datasetsList"
				:tab="layout.selectedTab"
				@closeModal="layout.dataset = false; layout.selectedTab = 'overview'; router.push('/datasets/')"
				@datasetUpdated="reloadDataset($event)"
				@datasetDeleted="layout.dataset = false; reset()"/>
		</div>

		<!-- Create dataset -->
		<div v-if="showCreateDataset">
			<CreateDatasetCmp 
				@closeModal="showCreateDataset = false"
				@datasetCreated="showCreateDataset = false; reset()"/>
		</div>

		<!-- Delete dataset -->
		<div v-if="showDeleteDataset">
			<DeleteDatasetCmp 
				:dataset="layout.selectedDataset"
				@closeModal="showDeleteDataset = false; layout.selectedDataset = false"
				@datasetDeleted="showDeleteDataset = false; layout.selectedDataset = false; reset()"/>
		</div>

		<!-- Download dataset -->
		<div v-if="showDownloadDataset">
			<DownloadDatasetCmp 
				:dataset="layout.selectedDataset"
				@closeModal="showDownloadDataset = false; layout.selectedDataset = false"/>
		</div>

	</div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } 		from "vue-router";
import cash 				from "cash-dom";

import DatasetCmp			from "@/components/manage-dataset/Main.vue";
import CreateDatasetCmp 	from "@/modals/dataset/CreateDataset.vue";
import DeleteDatasetCmp 	from "@/modals/dataset/DeleteDataset.vue";
import DownloadDatasetCmp 	from "@/modals/dataset/DownloadDataset.vue";

import * as rosepetalModel  from "rosepetal-model";
import { helper as $h } 	from "@/utils/helper";

const $dataset = rosepetalModel.default.dataset;

const router = useRouter();

const showHideDatasets 		= false;
const showCreateDataset 	= ref(false);
const showDeleteDataset 	= ref(false);
const showDownloadDataset 	= ref(false);

const dropdownRefs 			= ref({});
const activeDropdownId 		= ref(null);

const layout = ref({
	loading: 		false,
	options: {
		search: 	"",
		type: 		"",
		trained: 	"",
		order: 		"desc"
	},
    dataset: 			false,
	datasetsList: 		false,
	selectedDataset: 	false,
	selectedTab: 		'overview'
});

const handleClickOutside = (event) => {
	const currentRef = dropdownRefs.value[activeDropdownId.value];
	if (currentRef && !currentRef.contains(event.target)) {
		closeDropdown();
	}
}

const load = async () => {
    layout.value.loading = true;

    layout.value.datasetsList = await $dataset.list(layout.value.options);

	if (layout.value.options.trained) {
		layout.value.datasetsList = layout.value.datasetsList.filter(dataset => dataset.trained);
	}

	if (layout.value.options.search) {
		layout.value.datasetsList = layout.value.datasetsList.filter(dataset => 
			dataset.name.toLowerCase().includes(layout.value.options.search.toLowerCase())
		);
	}

    if (router.currentRoute.value.params.id) { 
        await loadDataset(router.currentRoute.value.params.id);
    }

    if (layout.value.datasetsList.length) {
        const promises = layout.value.datasetsList.map(dataset => {
            if (dataset) {
                return $dataset.getPreview(dataset.id)
                .then(resp => { if (resp.preview) {  dataset.preview = resp.preview; } });
            }
        });
        await Promise.all(promises);
    }

    layout.value.loading = false;
}

const loadDataset = async (datasetId, tab = false) => {
    let dsIndex = Object.values(layout.value.datasetsList).findIndex(d => d.id === datasetId);
    if (dsIndex !== -1) {
		layout.value.selectedTab 		= tab || 'overview';
		layout.value.dataset 			= layout.value.datasetsList[dsIndex];
        layout.value.dataset.idx 		= dsIndex;
		document.body.style.overflow 	= 'hidden';
    }
}

const reloadDataset = async (dataset) => {
	await $dataset.getResume(dataset);
	if (!dataset.preview) {
		const { preview } = await $dataset.getPreview(dataset.id);
		dataset.preview = preview;
	}
}

const reset	= async () => { 
    layout.value.options = { 
        search: 	"",
        type: 		"",
        trained: 	"",
        order: 		"desc"
    }
	await load();
}

const hideDatasets = async () => {
    await $dataset.hide();
}

const closeDropdown = () => {
	activeDropdownId.value = null;
}

onMounted(async () => { 
	await load();
    if (!router.currentRoute.value.params.id) {
		router.push('/datasets/');
	}

	document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
	document.removeEventListener('click', handleClickOutside);
});

window.onpopstate = async function() {
    let currentUrl   = window.location.pathname;
    currentUrl       = currentUrl.replace("/", "");
    let _cDs         = currentUrl.toString().split('/');

    if (_cDs[0] && _cDs[0] === 'datasets') {
        if (!_cDs[1]) {
            cash('#dataset-manage, #modal-image-editor').modal('hide') 
        }
        else if (_cDs.length == 2) {
            cash('#modal-image-editor').modal('hide'); 
            layout.value.editor.options.id = false;
            if (_cDs[1] !== layout.value.dataset.manage.id) await loadDataset(_cDs[1]);
        } else { console.log(_cDs); }
    }
}

let searchTimeout;

watch(() => [layout.value.options.type, layout.value.options.trained, layout.value.options.order], async () => {
	await load();
});

watch(() => layout.value.options.search, () => {
	clearTimeout(searchTimeout);
	searchTimeout = setTimeout(async () => {
		await load();
	}, 300);
});
</script>

<style>
#dataset-manage-page {
	height: 100vh; 
}

.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-content {
  display: none;
  position: absolute;
  right: 0;
  left: auto;
  background-color: #f1f1f1;
  min-width: 160px;
  box-shadow: 0px 8px 16px rgba(0,0,0,0.2);
  z-index: 1;
}

.dropdown:hover .dropdown-content {
	display: block;
}

.form-select.bg-blue-500 {
	background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
}

.form-select {
	background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
}


</style>